<template>
  <div class="camera-capture">
    <!-- 相机界面 - 默认显示，用灰色填充 -->
    <div class="camera-interface">
      <!-- 相机预览 -->
      <div class="camera-preview-container">
        <video
          ref="videoRef"
          class="camera-preview"
          :class="{ 'fullscreen': isFullscreen, 'has-stream': stream !== null }"
          autoplay
          playsinline
          webkit-playsinline
          x5-playsinline
          x5-video-player-type="h5"
          x5-video-player-fullscreen="false"
          muted
          preload="auto"
          style="display: block; visibility: visible; opacity: 1;"
          @loadeddata="onVideoLoaded"
          @canplay="onVideoCanPlay"
          @error="onVideoError"
        ></video>
        
        <!-- 加载状态指示器 -->
        <div v-if="isLoading" class="camera-loading">
          <span>正在启动相机...</span>
        </div>
        
        <!-- 默认占位符 -->
        <div v-if="!stream && !isLoading" class="camera-placeholder">
          <span class="placeholder-icon">📷</span>
          <p>点击"启动相机"开始拍照</p>
          <div class="placeholder-tips">
            <p>💡 使用提示：</p>
            <ul>
              <li>确保浏览器已获得相机权限</li>
              <li>在光线充足的环境下拍摄效果更佳</li>
              <li>保持设备稳定，避免模糊</li>
            </ul>
          </div>
        </div>
        
        <!-- 拍照控制 -->
        <div class="camera-controls">
          <!-- 启动相机按钮 -->
          <button
            v-if="!isCameraActive"
            class="camera-toggle-btn"
            @click="startCamera"
            :disabled="isLoading"
          >
            <span class="camera-icon">📷</span>
            {{ isLoading ? '启动相机中...' : '启动相机' }}
          </button>
          
          <!-- 拍照按钮和其他控制 -->
          <div v-if="isCameraActive" class="camera-active-controls">
            <!-- 相机信息显示 -->
            <div class="camera-info">
              <span class="info-item">
                <span class="info-label">分辨率:</span>
                <span class="info-value">{{ getVideoResolution }}</span>
              </span>
              <span class="info-item">
                <span class="info-label">镜头:</span>
                <span class="info-value">{{ getCameraTypeText }}</span>
              </span>
            </div>
            
            <div class="capture-section">
              <button class="capture-btn" @click="capturePhoto">
                <span class="capture-icon">📸</span>
              </button>
            </div>
            
            <div class="camera-actions">
              <button class="action-btn" @click="toggleFullscreen">
                <span class="action-icon">{{ isFullscreen ? '📱' : '🖥️' }}</span>
                {{ isFullscreen ? '退出全屏' : '全屏' }}
              </button>
              
              <button class="action-btn" @click="switchCamera">
                <span class="action-icon">🔄</span>
                切换镜头
              </button>
              
              <button class="action-btn close-btn" @click="stopCamera">
                <span class="action-icon">❌</span>
                关闭
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 照片预览 -->
      <div v-if="capturedPhoto" class="photo-preview">
        <h4>照片预览</h4>
        <div class="preview-container">
          <img :src="capturedPhoto" alt="拍摄的照片" class="preview-image" />
          <div class="preview-actions">
            <button class="preview-btn confirm-btn" @click="confirmPhoto">
              <span class="preview-icon">✅</span>
              确认使用
            </button>
            <button class="preview-btn retake-btn" @click="retakePhoto">
              <span class="preview-icon">🔄</span>
              重新拍摄
            </button>
          </div>
        </div>
      </div>
    </div>


    <!-- 错误提示 -->
    <div v-if="error" class="error-message">
      <p>{{ error }}</p>
      <div class="error-suggestions">
        <p><strong>建议解决方案：</strong></p>
        <ul>
          <li>检查浏览器是否允许访问相机</li>
          <li>确保没有其他应用正在使用相机</li>
          <li>尝试刷新页面重新授权</li>
          <li>检查设备是否连接了可用的摄像头</li>
        </ul>
      </div>
      <button class="retry-btn" @click="retryCamera">重试</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onUnmounted, computed } from 'vue'
import { useI18n } from '../composables/useI18n'

// 响应式数据
const videoRef = ref<HTMLVideoElement | null>(null)
const isCameraActive = ref(false)
const isLoading = ref(false)
const isFullscreen = ref(false)
const capturedPhoto = ref<string | null>(null)
const error = ref<string | null>(null)

// 多语言
const { language } = useI18n()

// 相机相关变量
let stream: MediaStream | null = null
let currentFacingMode: 'user' | 'environment' = 'environment'

// 启动相机
const startCamera = async () => {
  try {
    isLoading.value = true
    error.value = null
    
    // 检查浏览器支持性
    if (!checkCameraSupport()) {
      return
    }
    
    // 先尝试获取设备列表
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const videoDevices = devices.filter(device => device.kind === 'videoinput')
      
      if (videoDevices.length === 0) {
        throw new Error('未检测到任何相机设备')
      }
      
      console.log(`检测到 ${videoDevices.length} 个相机设备`)
    } catch (deviceErr) {
      console.warn('无法枚举相机设备:', deviceErr)
    }
    
    // 检查相机权限状态
    try {
      const permissionStatus = await navigator.permissions?.query({ name: 'camera' as PermissionName })
      if (permissionStatus?.state === 'denied') {
        throw new Error('相机权限已被拒绝，请在浏览器设置中启用相机权限')
      }
    } catch (permErr) {
      // 某些浏览器可能不支持权限查询，继续尝试
      console.warn('无法查询相机权限状态:', permErr)
    }
    
    // 请求相机权限，使用更兼容的配置
    const constraints = {
      video: {
        width: { ideal: 1920, min: 640 },
        height: { ideal: 1080, min: 480 },
        facingMode: currentFacingMode,
        // 添加更多兼容性选项
        frameRate: { ideal: 30, min: 15 }
      },
      audio: false
    }
    
    console.log('请求相机权限，配置:', constraints)
    stream = await navigator.mediaDevices.getUserMedia(constraints)
    
    if (videoRef.value) {
      const video = videoRef.value
      video.srcObject = stream
      
      // 设置视频属性以优化播放
      video.muted = true
      video.playsInline = true
      
      // 移动端优化：等待视频流有数据后再尝试播放
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('视频加载超时'))
        }, 15000) // 增加超时时间到15秒
        
        const onLoadedMetadata = () => {
          clearTimeout(timeout)
          video.removeEventListener('loadedmetadata', onLoadedMetadata)
          video.removeEventListener('error', onError)
          
          console.log(`视频尺寸: ${video.videoWidth}x${video.videoHeight}`)
          
          // 移动端：确保视频有实际像素数据
          const checkVideoReady = () => {
            if (video.videoWidth > 0 && video.videoHeight > 0) {
              resolve(true)
            } else {
              setTimeout(checkVideoReady, 100)
            }
          }
          checkVideoReady()
        }
        
        const onError = (err: Event) => {
          clearTimeout(timeout)
          video.removeEventListener('loadedmetadata', onLoadedMetadata)
          video.removeEventListener('error', onError)
          reject(new Error(`视频加载失败: ${err}`))
        }
        
        video.addEventListener('loadedmetadata', onLoadedMetadata)
        video.addEventListener('error', onError)
      })
      
      // 显式调用play()确保移动端播放
      try {
        await video.play()
        console.log('视频播放成功')
      } catch (playError) {
        console.warn('视频自动播放失败:', playError)
        // 移动端：如果自动播放失败，等待用户交互
        error.value = '请点击屏幕以启动相机预览'
        // 不返回，继续显示相机界面让用户手动点击
      }
      
      isCameraActive.value = true
      
      // 设置移动端优化
      setupMobileOptimizations()
    }
  } catch (err) {
    console.error('相机启动失败:', err)
    
    // 提供更详细的错误信息和解决建议
    if (err instanceof Error) {
      if (err.name === 'NotAllowedError') {
        error.value = '相机权限被拒绝。请点击地址栏左侧的相机图标，选择"允许"，然后刷新页面重试。'
      } else if (err.name === 'NotFoundError') {
        error.value = '未找到可用的相机设备。请确保相机已连接并未被其他应用占用。'
      } else if (err.name === 'NotSupportedError') {
        error.value = '您的设备或浏览器不支持相机功能。请尝试使用最新版本的Chrome、Firefox或Safari浏览器。'
      } else if (err.name === 'NotReadableError') {
        error.value = '相机设备正被其他应用占用。请关闭其他使用相机的应用（如视频会议软件），然后重试。'
      } else if (err.name === 'OverconstrainedError') {
        error.value = '相机无法满足请求的配置。请尝试刷新页面或使用其他相机。'
      } else {
        error.value = `相机启动失败: ${err.message}`
      }
    } else {
      error.value = '相机启动失败，请检查设备连接和浏览器权限设置。'
    }
  } finally {
    isLoading.value = false
  }
}

// 拍照
const capturePhoto = () => {
  if (!videoRef.value) return
  
  // 添加拍照动画效果
  const videoContainer = videoRef.value.parentElement
  if (videoContainer) {
    videoContainer.style.animation = 'flash 0.3s ease-out'
    setTimeout(() => {
      videoContainer.style.animation = ''
    }, 300)
  }
  
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  
  if (!context) return
  
  // 获取视频实际尺寸
  const video = videoRef.value
  const videoWidth = video.videoWidth || 1920
  const videoHeight = video.videoHeight || 1080
  
  // 设置画布尺寸为视频实际尺寸，保持最高质量
  canvas.width = videoWidth
  canvas.height = videoHeight
  
  // 绘制图像
  context.drawImage(video, 0, 0, videoWidth, videoHeight)
  
  // 添加时间戳水印
  const now = new Date()
  const locale = language.value === 'zh-CN' ? 'zh-CN' : language.value === 'de-DE' ? 'de-DE' : 'en-US'
  const timestamp = now.toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
  
  context.font = '16px Arial'
  context.fillStyle = 'rgba(255, 255, 255, 0.8)'
  context.strokeStyle = 'rgba(0, 0, 0, 0.8)'
  context.lineWidth = 3
  context.strokeText(timestamp, 20, videoHeight - 20)
  context.fillText(timestamp, 20, videoHeight - 20)
  
  // 转换为高质量JPEG
  capturedPhoto.value = canvas.toDataURL('image/jpeg', 0.95)
  
  // 播放拍照音效（如果浏览器支持）
  playCaptureSound()
}

// 播放拍照音效
const playCaptureSound = () => {
  try {
    // 创建一个简单的拍照音效
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
    const oscillator = audioContext.createOscillator()
    const gainNode = audioContext.createGain()
    
    oscillator.connect(gainNode)
    gainNode.connect(audioContext.destination)
    
    oscillator.frequency.value = 1000
    oscillator.type = 'sine'
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1)
    
    oscillator.start(audioContext.currentTime)
    oscillator.stop(audioContext.currentTime + 0.1)
  } catch (error) {
    // 静默处理音频错误
    console.log('无法播放拍照音效:', error)
  }
}

// 确认使用照片
const confirmPhoto = () => {
  if (capturedPhoto.value) {
    // 发射照片数据给父组件
    emit('photoCaptured', capturedPhoto.value)
    stopCamera()
  }
}

// 重新拍摄
const retakePhoto = () => {
  capturedPhoto.value = null
}

// 切换全屏
const toggleFullscreen = () => {
  if (!videoRef.value) return
  
  if (!isFullscreen.value) {
    if (videoRef.value.requestFullscreen) {
      videoRef.value.requestFullscreen()
    }
  } else {
    if (document.exitFullscreen) {
      document.exitFullscreen()
    }
  }
  
  isFullscreen.value = !isFullscreen.value
}

// 切换前后摄像头
const switchCamera = async () => {
  if (stream) {
    // 停止当前流
    stream.getTracks().forEach(track => track.stop())
    
    // 切换摄像头
    currentFacingMode = currentFacingMode === 'user' ? 'environment' : 'user'
    
    // 重新启动相机
    await startCamera()
  }
}

// 停止相机
const stopCamera = () => {
  if (stream) {
    stream.getTracks().forEach(track => track.stop())
    stream = null
  }
  
  if (videoRef.value) {
    videoRef.value.srcObject = null
  }
  
  isCameraActive.value = false
  capturedPhoto.value = null
  isFullscreen.value = false
}

// 重试相机
const retryCamera = () => {
  error.value = null
  startCamera()
}

// 移动端：添加用户交互触发播放
const handleUserInteraction = async () => {
  if (videoRef.value && videoRef.value.paused) {
    try {
      await videoRef.value.play()
      error.value = null
    } catch (err) {
      // 用户交互后播放失败，静默处理
    }
  }
}

// 检测移动端
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

// 移动端优化：添加触摸事件监听
const setupMobileOptimizations = () => {
  if (isMobileDevice() && videoRef.value) {
    // 添加触摸事件以触发播放
    videoRef.value.addEventListener('touchstart', handleUserInteraction)
    videoRef.value.addEventListener('click', handleUserInteraction)
  }
}

// 检查相机支持性
const checkCameraSupport = () => {
  const supportInfo = {
    hasMediaDevices: !!navigator.mediaDevices,
    hasGetUserMedia: !!navigator.mediaDevices?.getUserMedia,
    hasEnumerateDevices: !!navigator.mediaDevices?.enumerateDevices,
    userAgent: navigator.userAgent,
    isHTTPS: window.location.protocol === 'https:'
  }
  
  if (!supportInfo.hasMediaDevices || !supportInfo.hasGetUserMedia) {
    error.value = '您的浏览器不支持相机功能，请使用现代浏览器如 Chrome、Firefox、Safari 或 Edge'
    return false
  }
  
  if (!supportInfo.isHTTPS) {
    // 非 HTTPS 环境下相机功能可能受限
  }
  
  return true
}

// 事件发射器
const emit = defineEmits<{
  (e: 'photoCaptured', photoData: string): void
}>()

// 组件卸载时清理
onUnmounted(() => {
  // 移除移动端事件监听
  if (videoRef.value) {
    videoRef.value.removeEventListener('touchstart', handleUserInteraction)
    videoRef.value.removeEventListener('click', handleUserInteraction)
  }
  stopCamera()
})

// 视频加载事件处理
const onVideoLoaded = () => {
  // 视频数据加载完成
}

const onVideoCanPlay = () => {
  // 视频可以播放
}

const onVideoError = () => {
  // 视频加载错误
  error.value = '视频加载失败，请重试'
}

// 监听全屏变化
document.addEventListener('fullscreenchange', () => {
  isFullscreen.value = !!document.fullscreenElement
})

// 获取视频分辨率信息
const getVideoResolution = computed(() => {
  if (!videoRef.value || !videoRef.value.videoWidth) {
    return '检测中...'
  }
  return `${videoRef.value.videoWidth} × ${videoRef.value.videoHeight}`
})

// 获取镜头类型文本
const getCameraTypeText = computed(() => {
  return currentFacingMode === 'user' ? '前置' : '后置'
})
</script>

<style scoped>
.camera-capture {
  margin: 15px 0;
}

.camera-toggle-btn {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  gap: 8px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.camera-toggle-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
}

.camera-toggle-btn:disabled {
  background: #bdc3c7;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.camera-icon {
  font-size: 1.2rem;
}

.camera-interface {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.camera-preview-container {
  position: relative;
  margin-bottom: 20px;
  min-height: 300px; /* 确保容器有最小高度 */
  background: #1a1a1a; /* 深灰色背景，比纯黑更友好 */
  border-radius: 8px;
  overflow: hidden; /* 防止内容溢出 */
  align-items: center;
  justify-content: center;
  /* 确保容器可见 */
  visibility: visible !important;
  opacity: 1 !important;
  /* 强制硬件加速 */
  transform: translateZ(0);
  backface-visibility: hidden;
  perspective: 1000;
}

.camera-preview {
  width: 100%;
  max-width: 100%;
  height: 300px; /* 固定高度确保显示 */
  border-radius: 8px;
  background: #000;
  display: block;
  object-fit: cover; /* 使用cover填充容器 */
  /* 确保视频元素可见 */
  visibility: visible !important;
  opacity: 1 !important;
  z-index: 1;
}

/* 移动端优化：视频加载前的占位符 */
.camera-preview:not([srcObject]) {
  background: linear-gradient(45deg, #333 25%, #444 25%, #444 50%, #333 50%, #333 75%, #444 75%, #444);
  background-size: 20px 20px;
}

/* 确保视频加载后可见 */
.camera-preview[srcObject] {
  background: transparent !important;
}

/* 视频加载状态指示器 */
.camera-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 16px;
  z-index: 2;
  background: rgba(0, 0, 0, 0.7);
  padding: 10px 20px;
  border-radius: 20px;
}

/* 有视频流时的样式 */
.camera-preview.has-stream {
  background: transparent !important;
}

/* 强制视频元素可见 */
.camera-preview:not(:empty) {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

/* 防止视频被隐藏 */
.camera-preview-container:has(.camera-preview[srcObject]) {
  background: transparent !important;
}

/* 相机占位符 */
.camera-placeholder {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #999;
  z-index: 1;
}

.placeholder-icon {
  font-size: 3rem;
  display: block;
  margin-bottom: 10px;
}

.camera-placeholder p {
  margin: 0;
  font-size: 1rem;
  color: #666;
}

/* 相机信息显示 */
.camera-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  margin-bottom: 15px;
  font-size: 0.9rem;
  backdrop-filter: blur(5px);
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-label {
  color: #ccc;
  font-weight: 500;
}

.info-value {
  color: #fff;
  font-weight: 600;
}

/* 改进占位符样式 */
.placeholder-tips {
  margin-top: 15px;
  padding: 15px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  border-left: 4px solid #667eea;
}

.placeholder-tips p {
  margin: 0 0 10px 0;
  font-weight: 600;
  color: #667eea;
}

.placeholder-tips ul {
  margin: 0;
  padding-left: 20px;
  color: #666;
  font-size: 0.9rem;
}

.placeholder-tips li {
  margin-bottom: 5px;
  line-height: 1.4;
}

.camera-preview.fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  border-radius: 0;
  object-fit: contain; /* 全屏时保持比例 */
}

/* 全屏模式下的控制按钮容器 */
.camera-preview.fullscreen + .camera-controls {
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  background: rgba(0, 0, 0, 0.7);
  padding: 15px;
  border-radius: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* 全屏模式下的拍照按钮 */
.camera-preview.fullscreen + .camera-controls .capture-btn {
  width: 80px;
  height: 80px;
  font-size: 2.5rem;
  border-width: 6px;
  border-color: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

/* 全屏模式下的操作按钮 */
.camera-preview.fullscreen + .camera-controls .camera-actions {
  margin-top: 15px;
  gap: 15px;
}

.camera-preview.fullscreen + .camera-controls .action-btn {
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  border: none;
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 600;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

.camera-preview.fullscreen + .camera-controls .action-btn:hover {
  background: white;
  transform: translateY(-2px);
}

.camera-controls {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-top: 15px;
  width: 100%;
}

.camera-active-controls {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
}

.capture-section {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 10px;
}

.capture-btn {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border: 4px solid white;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.capture-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
}

.camera-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  color: #495057;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-btn:hover {
  background: #e9ecef;
  transform: translateY(-1px);
}

.close-btn {
  background: #dc3545;
  color: white;
  border-color: #dc3545;
}

.close-btn:hover {
  background: #c82333;
}

.photo-preview {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.photo-preview h4 {
  margin-bottom: 15px;
  color: #495057;
  text-align: center;
}

.preview-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.preview-image {
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.preview-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}

.preview-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.confirm-btn {
  background: #28a745;
  color: white;
}

.confirm-btn:hover {
  background: #218838;
  transform: translateY(-1px);
}

.retake-btn {
  background: #6c757d;
  color: white;
}

.retake-btn:hover {
  background: #5a6268;
  transform: translateY(-1px);
}


.error-message {
  background: #f8d7da;
  color: #721c24;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #f5c6cb;
  text-align: left;
}

.error-suggestions {
  margin-top: 10px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 4px;
}

.error-suggestions p {
  margin-bottom: 8px;
  font-weight: 600;
}

.error-suggestions ul {
  margin: 0;
  padding-left: 20px;
}

.error-suggestions li {
  margin-bottom: 4px;
  line-height: 1.4;
}

.retry-btn {
  margin-top: 10px;
  padding: 8px 16px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  font-weight: 500;
}

.retry-btn:hover {
  background: #c82333;
}

/* 响应式设计 - 移动端拍照按钮独立一行 */
@media (max-width: 768px) {
  .camera-toggle-btn {
    width: 100%;
    max-width: 300px;
    padding: 16px 24px;
    font-size: 1.1rem;
    justify-content: center;
    margin: 0 auto;
  }
  
  .camera-controls {
    justify-content: center;
    align-items: center;
  }
  
  .camera-active-controls {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 30px; /* 大幅增加拍照按钮和控制按钮之间的间距 */
    width: 100%;
  }
  
  .capture-section {
    display: flex;
    justify-content: center;
    width: 100%;
    margin-bottom: 0;
  }
  
  .capture-btn {
    width: 85px;
    height: 85px;
    font-size: 2.2rem;
    border-width: 6px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
  }
  
  .camera-actions {
    display: flex;
    flex-direction: row;
    gap: 12px;
    max-width: 100%;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    width: 100%;
  }
  
  .action-btn {
    flex: 1;
    min-width: 110px;
    padding: 14px 16px;
    font-size: 0.95rem;
    font-weight: 600;
    justify-content: center;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  .preview-actions {
    flex-direction: row;
    gap: 10px;
    width: 100%;
    justify-content: center;
  }
  
  .preview-btn {
    flex: 1;
    justify-content: center;
    max-width: 200px;
  }
}

@media (max-width: 480px) {
  .camera-interface {
    padding: 16px;
  }
  
  .capture-btn {
    width: 80px;
    height: 80px;
    font-size: 2rem;
    border-width: 5px;
  }
  
  .camera-actions {
    gap: 10px;
  }
  
  .action-btn {
    padding: 12px 14px;
    font-size: 0.9rem;
    min-width: 90px;
  }
  
  .action-icon {
    font-size: 1rem;
  }
}

@media (max-width: 360px) {
  .camera-interface {
    padding: 12px;
  }
  
  .capture-btn {
    width: 75px;
    height: 75px;
    font-size: 1.8rem;
    border-width: 4px;
  }
  
  .camera-actions {
    gap: 8px;
  }
  
  .action-btn {
    padding: 10px 12px;
    font-size: 0.85rem;
    min-width: 80px;
  }
  
  .action-icon {
    font-size: 0.9rem;
  }
}
/* 拍照闪光动画 */
@keyframes flash {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
    background-color: rgba(255, 255, 255, 0.8);
  }
  100% {
    opacity: 1;
  }
}

/* 相机预览容器添加过渡效果 */
.camera-preview-container {
  transition: all 0.3s ease;
}

/* 改进拍照按钮样式 */
.capture-btn {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border: 4px solid white;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.capture-btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.capture-btn:active::before {
  width: 100px;
  height: 100px;
}

.capture-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
}

/* 改进照片预览样式 */
.preview-image {
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.preview-image:hover {
  transform: scale(1.02);
}

/* 添加加载状态样式 */
.camera-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 16px;
  z-index: 2;
  background: rgba(0, 0, 0, 0.7);
  padding: 10px 20px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.camera-loading::after {
  content: '';
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

</style>